import { parseUserAgent } from '@/utils/userAgentParser';

/**
 * セッション管理用ストレージ
 */
const SESSION_STORAGE_KEY = 'activity_session';

interface SessionData {
  sessionId: string;
  startTime: number;
  lastActionTime: number;
  actionCount: number;
  apiCallCount: number;
}

/**
 * セッションIDを取得または生成
 */
function getOrCreateSession(): SessionData {
  const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);

  if (stored) {
    const session = JSON.parse(stored);
    return session;
  }

  // 新規セッション作成
  const newSession: SessionData = {
    sessionId: `${Date.now()}-${Math.random().toString(36).substring(7)}`,
    startTime: Date.now(),
    lastActionTime: Date.now(),
    actionCount: 0,
    apiCallCount: 0
  };

  sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newSession));
  return newSession;
}

/**
 * セッション情報を更新
 */
function updateSession(): SessionData {
  const session = getOrCreateSession();
  const now = Date.now();
  const previousActionTime = session.lastActionTime;

  session.actionCount++;
  session.lastActionTime = now;

  sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));

  // 前回のアクション時刻も返す（リスクスコア計算用）
  return {
    ...session,
    lastActionTime: previousActionTime  // 前回の時刻を返す
  };
}

/**
 * ページ滞在時間を計算
 */
function getPageDuration(): number {
  const navigationStart = performance.timing?.navigationStart || Date.now();
  return Math.floor((Date.now() - navigationStart) / 1000);
}

/**
 * リスクスコア計算（クライアントサイド）
 */
function calculateRiskScore(data: {
  userAgent: {
    isBot: boolean;
  };
  timeSinceLastAction: number;
  pageDuration: number;
  sessionData: SessionData;
}): { riskScore: number; riskFactors: string[]; isSuspicious: boolean } {
  const factors: string[] = [];
  let score = 0;

  // 1. ボットUser Agent判定（50点）
  if (data.userAgent.isBot) {
    score += 50;
    factors.push('BOT_USER_AGENT');
  }

  // 2. 高速アクセス（30点）
  if (data.timeSinceLastAction < 0.5 && data.sessionData.actionCount > 1) {
    score += 30;
    factors.push('RAPID_ACCESS');
  }

  // 3. ページ滞在時間異常（20点）
  if (data.pageDuration < 1 && data.sessionData.actionCount > 1) {
    score += 20;
    factors.push('NO_PAGE_INTERACTION');
  }

  // 4. 大量API呼び出し（40点）
  if (data.sessionData.apiCallCount > 100) {
    score += 40;
    factors.push('EXCESSIVE_API_CALLS');
  }

  return {
    riskScore: Math.min(score, 100),
    riskFactors: factors,
    isSuspicious: score >= 50
  };
}

/**
 * ネットワーク情報を取得
 */
function getNetworkInfo() {
  // @ts-ignore - Network Information API is not in TypeScript types yet
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  if (!connection) {
    return undefined;
  }

  return {
    connection_type: connection.effectiveType || undefined,  // '4g', '3g', '2g', 'slow-2g'
    downlink_mbps: connection.downlink || undefined,
    rtt_ms: connection.rtt || undefined
  };
}

/**
 * デバイス詳細情報を取得
 */
function getDeviceInfo() {
  return {
    screen_width: screen.width,
    screen_height: screen.height,
    screen_resolution: `${screen.width}x${screen.height}`,
    has_touch: 'ontouchstart' in window || navigator.maxTouchPoints > 0
  };
}

/**
 * 表示言語を取得
 */
function getDisplayLanguage(): string {
  // localStorageに保存されている言語を優先、なければブラウザのデフォルト言語
  return localStorage.getItem('language') || navigator.language;
}

/**
 * ブラウザから取得できる環境情報を収集（セキュリティ重視）
 */
export function collectBrowserEnvironment() {
  const ua = navigator.userAgent;
  const session = updateSession();
  const parsedUA = parseUserAgent(ua);

  const timeSinceLastAction = (Date.now() - session.lastActionTime) / 1000;
  const pageDuration = getPageDuration();

  // リスクスコア計算
  const currentSession = getOrCreateSession();
  const { riskScore, riskFactors, isSuspicious } = calculateRiskScore({
    userAgent: parsedUA,
    timeSinceLastAction,
    pageDuration,
    sessionData: currentSession
  });

  // ネットワーク情報
  const networkInfo = getNetworkInfo();

  // デバイス情報
  const deviceInfo = getDeviceInfo();

  // 表示言語
  const displayLanguage = getDisplayLanguage();

  return {
    // セキュリティ情報
    security: {
      ip_address_masked: 'client-side',  // Edge Functionで設定
      risk_score: riskScore,
      risk_factors: riskFactors,
      is_suspicious: isSuspicious
    },

    // User Agent情報
    user_agent: {
      raw: ua,
      browser: parsedUA.browser,
      browser_version: parsedUA.browserVersion,
      os: parsedUA.os,
      os_version: parsedUA.osVersion,
      device_type: parsedUA.deviceType,
      is_bot: parsedUA.isBot
    },

    // セッション情報
    session: {
      session_id: currentSession.sessionId,
      time_since_last_action: timeSinceLastAction,
      action_count_in_session: currentSession.actionCount,
      page_duration: pageDuration
    },

    // ページ情報
    page: {
      url: window.location.href,
      referer: document.referrer || undefined
    },

    // ネットワーク情報（利用可能な場合のみ）
    ...(networkInfo && { network: networkInfo }),

    // デバイス情報
    device: deviceInfo,

    // 表示言語
    display_language: displayLanguage,

    platform: 'Web' as const
  };
}

/**
 * API呼び出しカウント更新
 */
export function incrementApiCallCount(): void {
  const session = getOrCreateSession();
  session.apiCallCount++;
  sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
}
